/**
 * autoSuggest — 3-pass auto-fill for OO Расстановка (B.12-R8.8). Ported from v3
 * utils/autoSuggest.ts to the web camelCase types. Hard rules: never exceed
 * operatorsNeeded, no overlapping games (+90min between branches), last slot
 * prefers an exam-passed op (rate ≥ 600), per-slot gender via scoreOperator's
 * crewGenders. Returns the filled AssignMap (mutates a local copy only).
 */
import {
  scoreOperator, type BookingForAssign, type OperatorForAssign, type HallAdminMap,
  type AssignMap, type ScoringConfig, DEFAULT_CONFIG,
} from "./scoring";

const EXAM_RATE = 600;

/* ── R8.8: human weights (0..100) → ScoringConfig, strategy presets ──────── */

export type WeightKey = "economy" | "grade" | "workload" | "overlap" | "offSchedule";
export type Weights = Record<WeightKey, number>;
export type StrategyId = "cost" | "best" | "even" | "custom";

export const WEIGHT_LABELS: Record<WeightKey, string> = {
  economy: "Экономия на зарплате",
  grade: "Мастерство-грейд",
  workload: "Равномерность нагрузки",
  overlap: "Избегать пересечений",
  offSchedule: "Штраф вне графика",
};

export const STRATEGIES: { id: Exclude<StrategyId, "custom">; label: string; weights: Weights }[] = [
  { id: "cost", label: "Минимум затрат", weights: { economy: 90, grade: 30, workload: 50, overlap: 80, offSchedule: 70 } },
  { id: "best", label: "Лучшие", weights: { economy: 20, grade: 90, workload: 40, overlap: 80, offSchedule: 70 } },
  { id: "even", label: "Ровные зарплаты", weights: { economy: 40, grade: 40, workload: 90, overlap: 80, offSchedule: 70 } },
];

export const DEFAULT_WEIGHTS: Weights = STRATEGIES[2]!.weights;

/** Map 0..100 human sliders onto the numeric ScoringConfig. */
export function weightsToConfig(w: Weights): ScoringConfig {
  const clamp = (n: number) => Math.max(0, Math.min(100, n)) / 100;
  return {
    ...DEFAULT_CONFIG,
    rateDiv: Math.round(20 - clamp(w.economy) * 15), // higher economy → cheaper preferred
    noRate: Math.round(clamp(w.grade) * 160),
    workloadMul: Math.round(clamp(w.workload) * 100),
    overlap: Math.round(200 + clamp(w.overlap) * 600),
    unavailable: Math.round(clamp(w.offSchedule) * 400),
  };
}

function timeToMin(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
function occupancyMin(b: BookingForAssign): number {
  return b.duration + b.resetTime + 15;
}
function overlaps(a: BookingForAssign, b: BookingForAssign): boolean {
  const s1 = timeToMin(a.time), e1 = s1 + occupancyMin(a);
  const s2 = timeToMin(b.time), e2 = s2 + occupancyMin(b);
  const travel = a.branchId !== b.branchId ? 90 : 0;
  return s1 - travel < e2 && s2 - travel < e1;
}
function opBookings(opId: string, map: AssignMap, bookings: BookingForAssign[]): BookingForAssign[] {
  const out: BookingForAssign[] = [];
  for (const [bid, ops] of Object.entries(map)) {
    if (ops.includes(opId)) {
      const bk = bookings.find((b) => b.id === bid);
      if (bk) out.push(bk);
    }
  }
  return out;
}
function crewHasExam(current: string[], operators: OperatorForAssign[], questId: string): boolean {
  return current.some((id) => {
    const op = operators.find((o) => o.id === id);
    return op && (op.rates[questId] ?? 0) >= EXAM_RATE;
  });
}
function gendersOf(ids: string[], byId: Map<string, OperatorForAssign>): string[] {
  return ids.map((id) => byId.get(id)?.gender ?? "").filter(Boolean);
}

export interface AutoResult {
  assignMap: AssignMap;
  filled: number;
  unfilled: number;
}

export function autoSuggest(args: {
  bookings: BookingForAssign[];
  operators: OperatorForAssign[];
  assignMap: AssignMap;
  hallAdmins: HallAdminMap;
  config?: ScoringConfig;
  useGender?: boolean;
  requireExam?: boolean;
}): AutoResult {
  const useGender = args.useGender ?? true;
  const requireExam = args.requireExam ?? true;
  const bookings = useGender ? args.bookings : args.bookings.map((b) => ({ ...b, crewRule: undefined }));
  const { operators, hallAdmins } = args;
  const cfg = args.config ?? DEFAULT_CONFIG;
  const byId = new Map(operators.map((o) => [o.id, o]));
  const map: AssignMap = {};
  for (const [k, v] of Object.entries(args.assignMap)) map[k] = [...v];

  const sorted = [...bookings].sort((a, b) => {
    const aN = (map[a.id] ?? []).length;
    const bN = (map[b.id] ?? []).length;
    if (aN === 0 && bN > 0) return -1;
    if (bN === 0 && aN > 0) return 1;
    return a.time.localeCompare(b.time);
  });

  for (let pass = 0; pass < 3; pass++) {
    for (const booking of sorted) {
      const current = map[booking.id] ?? [];
      if (current.length >= booking.operatorsNeeded) continue;
      const isLastSlot = current.length === booking.operatorsNeeded - 1;
      const hasExam = crewHasExam(current, operators, booking.questId);
      const crewGenders = gendersOf(current, byId);

      const cands = operators
        .filter((op) => {
          if (current.includes(op.id)) return false;
          if (opBookings(op.id, map, bookings).some((mg) => overlaps(mg, booking))) return false;
          if (requireExam && isLastSlot && !hasExam && (op.rates[booking.questId] ?? 0) < EXAM_RATE) return false;
          return true;
        })
        .map((op) => {
          const result = scoreOperator({ operator: op, booking, assignMap: map, allBookings: bookings, hallAdmins, crewGenders, config: cfg });
          if (!hasExam && (op.rates[booking.questId] ?? 0) >= EXAM_RATE) result.score -= 50;
          return { op, result };
        })
        .filter((c) => c.result.suitable)
        .sort((a, b) => a.result.score - b.result.score);

      let best = cands[0];
      if (!best && isLastSlot && !hasExam) {
        best = operators
          .filter((op) => !current.includes(op.id) && !opBookings(op.id, map, bookings).some((mg) => overlaps(mg, booking)))
          .map((op) => ({ op, result: scoreOperator({ operator: op, booking, assignMap: map, allBookings: bookings, hallAdmins, crewGenders, config: cfg }) }))
          .filter((c) => c.result.suitable)
          .sort((a, b) => a.result.score - b.result.score)[0];
      }
      if (best) (map[booking.id] ??= []).push(best.op.id);
    }
  }

  let filled = 0, unfilled = 0;
  for (const b of bookings) {
    const n = (map[b.id] ?? []).length;
    filled += Math.min(n, b.operatorsNeeded);
    unfilled += Math.max(0, b.operatorsNeeded - n);
  }
  return { assignMap: map, filled, unfilled };
}
